import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Icons
import LockOutlined from '@ant-design/icons/LockOutlined';
import ThunderboltFilled from '@ant-design/icons/ThunderboltFilled';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';

// Context
import { useSubscription } from 'contexts/SubscriptionContext';

// ==============================|| LOCKED MENU PROMPT MODAL ||============================== //

export default function LockedMenuPromptModal() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { lockedModal, closeLockedModal, plans, openCheckout } = useSubscription();

  const { open, module } = lockedModal;
  if (!open || !module) return null;

  const targetTier = module.minimumTier || 'professional';
  const targetPlan = plans.find((p) => p.tier === targetTier) || plans[1];

  const handleUpgradeClick = () => {
    closeLockedModal();
    openCheckout(targetPlan);
  };

  const handleViewAllPlans = () => {
    closeLockedModal();
    navigate('/subscription');
  };

  return (
    <Dialog
      open={open}
      onClose={closeLockedModal}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2.5,
          textAlign: 'center'
        }
      }}
    >
      <DialogContent sx={{ p: 1 }}>
        <Stack spacing={2.5} alignItems="center">
          {/* Lock Icon Badge */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'warning.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'warning.dark'
            }}
          >
            <LockOutlined style={{ fontSize: 32 }} />
          </Box>

          {/* Heading */}
          <Box>
            <Typography variant="h3" fontWeight={800} color="text.primary">
              Unlock {module.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {module.description || 'This advanced clinic module is part of our premium clinic plans.'}
            </Typography>
          </Box>

          {/* Tier Required Box */}
          <Box
            sx={{
              width: '100%',
              p: 2,
              borderRadius: 2,
              bgcolor: 'grey.50',
              border: '1.5px solid',
              borderColor: 'primary.lighter',
              textAlign: 'left'
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                Required Plan:
              </Typography>
              <Chip
                size="small"
                label={`${targetPlan.name} Plan`}
                color="primary"
                sx={{ fontWeight: 800, height: 22 }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Starting at <strong>{targetPlan.pricing.monthly.displayPrice}/mo</strong> (or {targetPlan.pricing.yearly.monthlyEquivalent} billed annually).
            </Typography>

            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleFilled style={{ fontSize: 13, color: theme.palette.success.main }} />
                <Typography variant="caption" color="text.primary">Instant access upon activation</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleFilled style={{ fontSize: 13, color: theme.palette.success.main }} />
                <Typography variant="caption" color="text.primary">No setup fee or lock-in period</Typography>
              </Stack>
            </Stack>
          </Box>

          {/* CTAs */}
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              onClick={handleUpgradeClick}
              startIcon={<ThunderboltFilled />}
              sx={{ py: 1.25, fontWeight: 700, borderRadius: 2 }}
            >
              Upgrade to {targetPlan.name}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleViewAllPlans}
              sx={{ py: 1, fontWeight: 600, borderRadius: 2 }}
            >
              Compare All Plans
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
